import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ClipboardList } from "lucide-react";
import { ServiceCard } from "@/components/Services";
import { BUDGET_OPTIONS } from "@/data/services";

const STORAGE_KEY = "exclusive-reykjavik-list";

export default function BudgetPage() {
  const [selected, setSelected] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(selected));
  }, [selected]);

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  return (
    <div data-testid="budget-page" className="min-h-screen bg-[#070709] text-neutral-100">
      <div className="noise-overlay" />
      <div className="mx-auto max-w-7xl px-5 py-14 sm:px-10 sm:py-20">
        <div className="flex items-center justify-between">
          <Link
            to="/"
            data-testid="budget-back-link"
            className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.3em] text-neutral-400 transition-colors hover:text-amber-200"
          >
            <ArrowLeft className="h-4 w-4" /> Back to the full catalogue
          </Link>
          <img src="/assets/logo.png" alt="Exclusive Reykjavik" className="h-12 w-auto" />
        </div>

        <motion.div
          className="mt-14"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <p className="text-xs font-semibold uppercase tracking-[0.35em] text-amber-300/80">The Smart-Lux Collection</p>
          <h1 className="mt-4 font-luxe text-3xl uppercase leading-[1.2] tracking-[0.12em] text-neutral-50 sm:text-5xl">
            Budget options.
            <br />
            <span className="font-serif normal-case italic text-amber-200">Same island, gentler numbers.</span>
          </h1>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-neutral-400">
            Four-star hotels, your own wheels, campers under the midnight sun — picked with the same care,
            priced for a different kind of trip. Everything you tap joins the same concierge list.
          </p>
          <p data-testid="budget-selected-count" className="mt-6 inline-block border border-amber-500/30 px-5 py-3 text-xs uppercase tracking-[0.25em] text-amber-200/90">
            {selected.length} on your list
          </p>
        </motion.div>

        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8">
          {BUDGET_OPTIONS.map((s, i) => (
            <ServiceCard key={s.id} service={s} selected={selected.includes(s.id)} onToggle={toggle} index={i} />
          ))}
        </div>

        <div className="mt-16 border border-amber-500/20 bg-[#0c0c10] p-8 text-center sm:p-12">
          <ClipboardList className="mx-auto h-6 w-6 text-amber-400/80" />
          <p className="mt-4 font-serif text-xl italic text-neutral-200">
            Everything you picked here waits on your concierge list.
          </p>
          <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-neutral-500">
            Head back to the main page and press "Your List" — add your dates and details, and our desk takes it from there.
          </p>
          <Link
            to="/"
            data-testid="budget-continue-link"
            className="mt-8 inline-block bg-amber-500 px-8 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-black transition-colors hover:bg-amber-300"
          >
            Continue to Your List
          </Link>
        </div>
      </div>
    </div>
  );
}
